import { MAX_MATERIAL_BYTES, formatOfContentType } from '../core/materials';

/**
 * What every way an upload can be refused says, and the one cheap check the browser runs
 * before any bytes move. Shared by the single-file form (`MaterialUpload`) and the upload
 * panel, so a refused file reads the same sentence whichever way it was chosen.
 *
 * Pure: a file's name, size and declared type in, a reason or a sentence out.
 */

/** One sentence, one spelling of it, wherever materials appear (R15). */
export const STUDENT_RECORDS_NOTICE =
  'Teacher-authored instructional materials only. Do not upload student work, student '
  + 'records, rosters, or personnel evaluations.';

/** What the file picker offers. The Worker re-decides from the bytes either way. */
export const ACCEPTED_FILE_TYPES = '.pdf,.docx,.pptx,.png,.jpg,.jpeg,.txt';

/** The upload rate limit is 10 per 60 s (`ACCEPT_LIMIT`), so one drop takes no more than that. */
export const MAX_FILES_PER_ADD = 10;

const MEGABYTE = 1024 * 1024;

/**
 * Keyed by the `reason` the Worker returns (`worker/handlers/materials.ts`) — those strings
 * are the API this file reads, and renaming one there is a breaking change here. The last
 * three are the browser's own: the panel's cap, a group that answered 404, and a reply that
 * could not be read at all.
 */
export const UPLOAD_REFUSAL_COPY: Readonly<Record<string, string>> = {
  material_too_large: `That file is over ${Math.round(MAX_MATERIAL_BYTES / MEGABYTE)} MB. Share a single document rather than a whole unit.`,
  unsupported_type: 'Collab takes PDF, Word, PowerPoint, PNG, JPEG and plain text files.',
  content_mismatch: 'That file does not match its type — a PDF that is not really a PDF, or text that is not UTF-8. Re-export it and try again.',
  empty: 'That file is empty.',
  office_macros: 'That file has macros. Save it again as .docx or .pptx without macros, then upload it.',
  office_linked_content: 'That file links to or embeds content from outside it. Remove linked or embedded objects, then upload it again.',
  material_limit_reached: 'This group is holding as many materials as it can. Delete one to make room.',
  storage_limit_reached: 'This group has used all of its storage. Delete a material to make room.',
  // The counting stream in front of the parser tripped, or the connection dropped part way.
  length_required: 'The upload stopped before it finished. Try it again.',
  network: 'The upload stopped before it finished. Your file is still selected — try again.',
  rate_limited: 'That is a lot of uploads in a row. Wait a moment and try again.',
  invalid_title: 'Give the material a title.',
  invalid_note: 'That note is too long.',
  invalid_file: 'Choose a file to share.',
  invalid_body: 'That upload could not be read. Try it again.',
  too_many_files: `Up to ${MAX_FILES_PER_ADD} files at a time. Add this one again in a minute.`,
  not_found: 'This group is no longer open to you, so the file was not shared.',
};

const FALLBACK = 'That file was refused. Try a PDF, Word, PowerPoint, image or text file.';

export const uploadRefusalCopy = (reason: string | null | undefined): string | null => {
  if (!reason) return null;
  return UPLOAD_REFUSAL_COPY[reason] ?? FALLBACK;
};

/**
 * The panel's wording. Only `network` differs: in the panel nothing stays selected, so the
 * form's "your file is still selected" would be a promise the panel cannot keep.
 */
export const queueRefusalCopy = (reason: string): string =>
  reason === 'network'
    ? 'The upload stopped before it finished. Add the file again.'
    : UPLOAD_REFUSAL_COPY[reason] ?? FALLBACK;

/**
 * The cheap half of `decideUpload`, run before any bytes move.
 *
 * Size and declared type only — the signature check needs the leading bytes and belongs to
 * the Worker, which does not trust this check anyway. This exists so a teacher hears about a
 * 40 MB file now rather than after uploading it.
 */
export const clientRefusal = (file: { size: number; type: string }): string | null =>
  file.size === 0 ? 'empty'
    : file.size > MAX_MATERIAL_BYTES ? 'material_too_large'
      : formatOfContentType(file.type) === null ? 'unsupported_type'
        : null;

/** A file's name without its extension: what a title defaults to. */
export const defaultTitle = (filename: string): string => filename.replace(/\.[^.]+$/, '');
